// src/pages/violations/ViolationsEntry/ViolationsTable.jsx

import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SearchIcon from '@mui/icons-material/Search';

// If you use a "ViewViolationDeviceModal":
import ViewViolationDeviceModal from '../ViolationsList/ViewViolationsDeviceModal';

import API_BASE_URL from '../../../../Constants';

/**
 *  Helper parse functions
 */
const parseDevicePartCode = (label) => {
  if (!label) return '';
  const match = label.match(/^(\d{1,3})\.\s/);
  return match ? match[1] : '';
};
const parseViolatingConditionCode = (label) => {
  if (!label) return '';
  const match = label.match(/^([A-Za-z]+)\.\s/);
  return match ? match[1] : '';
};
const parseSuggestedRemedyCode = (label) => {
  if (!label) return '';
  const match = label.match(/^(\d{1,3})\.\s/);
  return match ? match[1] : '';
};

/**
 *  The table component that just displays the parent's "violations".
 *  The "View" button calls "onViewViolations" to let the parent fetch if needed.
 */
export default function ViolationsTable({
  violations = [],
  inspections = [],
  selectedInspectionId,
  onSelectInspection,
  devices = [],
  selectedDeviceId,
  onSelectDevice,
  onViewViolations,
}) {
  // We do local searching/filtering on the "violations" we get from props
  const [filteredViolations, setFilteredViolations] = useState([]);

  // Searching
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Table/Card
  const [viewMode, setViewMode] = useState('table');

  // "View" modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedViolationId, setSelectedViolationId] = useState(null);

  // Whenever parent's "violations" changes => reset filteredViolations
  useEffect(() => {
    setFilteredViolations(violations);
  }, [violations]);

  // 1) "View" => parent fetch
  const handleViewClickFetch = () => {
    if (!selectedInspectionId || !selectedDeviceId) {
      alert('Please choose an inspection and device first!');
      return;
    }
    // Let the parent do the actual fetch
    onViewViolations?.();
  };

  // 2) Searching + filtering
  const handleSearch = () => {
    if (!searchInput && statusFilter === 'All') {
      setFilteredViolations(violations);
      return;
    }

    const lowerVal = searchInput.toLowerCase();
    const newFiltered = violations.filter((vio) => {
      const matchesText =
        !searchInput || JSON.stringify(vio).toLowerCase().includes(lowerVal);

      const lowerStatus = (vio.itemStatus || '').toLowerCase();
      const isMustRemedy = lowerStatus.includes('must remedy');
      const isRemedied = lowerStatus.includes('remedied');

      const matchesStatus =
        statusFilter === 'All' ||
        (statusFilter === 'Open' && isMustRemedy) ||
        (statusFilter === 'Closed' && isRemedied);

      return matchesText && matchesStatus;
    });

    setFilteredViolations(newFiltered);
  };

  // Whenever statusFilter changes => re-run handleSearch
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line
  }, [statusFilter]);

  // 3) Excel export
  const handleExcelExport = () => {
    if (!filteredViolations.length) {
      alert('No data to export!');
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(filteredViolations);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Violations');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'Violations.xlsx');
  };

  // 4) Modal open/close
  const handleOpenModal = (violationId) => {
    setSelectedViolationId(violationId);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setSelectedViolationId(null);
    setIsModalOpen(false);
  };

  // Summation
  const totalCost = filteredViolations.reduce(
    (sum, vio) => sum + Number(vio.estimatedAmount || 0),
    0
  );

  // Device label
  let deviceLabel = '';
  if (selectedDeviceId === 'ALL') {
    deviceLabel = 'All Devices';
  } else if (selectedDeviceId) {
    deviceLabel = `Device #${selectedDeviceId}`;
  }

  // "View" button => disabled if no inspection/device
  const isViewDisabled = !selectedInspectionId || !selectedDeviceId;

  return (
    <div className="flex flex-col w-full">
      {/* TOP BAR (title, dropdowns, etc.) */}
      <div className="bg-white rounded-md w-full p-2 border-b border-gray-300">
        <div className="flex items-center justify-between">
          {/* LEFT: Title, Inspection & Device dropdowns */}
          <div className="flex items-center space-x-3">

            <label className="font-bold text-gray-700 pl-8">Inspections:</label>
            <select
              className="border-2 border-sss-blue-sel rounded-lg px-3 py-2 text-gray-700"
              style={{ minWidth: '150px' }}
              value={selectedInspectionId}
              onChange={(e) => onSelectInspection(e.target.value)}
            >
              <option value="">Select Inspection</option>
              {inspections.map((insp) => (
                <option key={insp.inspectionId} value={insp.inspectionId}>
                  {insp.inspectionNumber ? insp.inspectionNumber : `ID ${insp.inspectionId}`}
                </option>
              ))}
            </select>

            <label className="font-bold text-gray-700 pl-8">Devices:</label>
            <select
              className="border-2 border-sss-blue-sel rounded-lg px-3 py-2 text-gray-700"
              style={{ minWidth: '150px' }}
              value={selectedDeviceId}
              onChange={(e) => onSelectDevice(e.target.value)}
              disabled={!selectedInspectionId}
            >
              <option value="">Select Device</option>
              <option value="ALL">All Devices</option>
              {devices.map((dev) => (
                <option key={dev.deviceId} value={dev.deviceId}>
                  {`#${dev.deviceId} ${dev.deviceName ? '- ' + dev.deviceName : ''}`}
                </option>
              ))}
            </select>

            <button
              onClick={handleViewClickFetch}
              disabled={isViewDisabled}
              className={
                isViewDisabled
                  ? 'px-4 py-2 border-2 border-gray-300 bg-gray-200 text-gray-400 rounded cursor-not-allowed'
                  : 'px-4 py-2 border-2 border-gray-900 bg-gray-800 text-white font-bold rounded-lg hover:bg-black transition'
              }
            >
              View
            </button>
          </div>

          {/* MIDDLE: Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="font-bold text-gray-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg font-bold text-gray-700"
            >
              <option value="All">All</option>
              <option value="Open">Open / Must Remedy</option>
              <option value="Closed">Closed / Remedied</option>
            </select>
          </div>

          {/* RIGHT: Search, Excel, Table/Card */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={handleSearch}
                className="px-3 py-2 text-gray-500 border-r border-gray-300 bg-gray-50 hover:bg-gray-100 hover:text-blue-600"
              >
                <SearchIcon fontSize="small" />
              </button>
              <input
                type="text"
                className="px-3 py-2 w-full text-sm focus:outline-none"
                placeholder="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </div>

            {/* Excel */}
            <button
              onClick={handleExcelExport}
              className="px-4 py-2 border-2 border-gray-500 text-gray-700 font-bold rounded-lg hover:bg-gray-500 hover:text-white"
            >
              Excel
            </button>

            {/* Table / Card */}
            <select
              className="px-4 py-2 border-2 border-gray-500 rounded-lg font-bold text-gray-700"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="table">Table View</option>
              <option value="card">Card View</option>
            </select>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT: Table or Card */}
      <div className="flex-1 flex flex-col relative">
        {/* TABLE VIEW */}
        {viewMode === 'table' && (
          <div className="overflow-auto flex-1 px-4 pb-4">
            <table className="min-w-full mt-4 border-2 border-gray-200 rounded-lg">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">P</th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">V</th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">R</th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Actions
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Violation ID
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Inspection #
                  </th>
                  {/*
                    OPTIONAL: If your violation object includes buildingSite & agency,
                    add columns for them here:
                  */}
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Building Site
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Agency
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Violation Date
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Condition
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Item Status
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Additional Comment
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Resolution
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm">
                    Cost Amount
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm">
                {filteredViolations.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="px-4 py-4 text-center">
                      No violations yet
                    </td>
                  </tr>
                ) : (
                  filteredViolations.map((vio, idx) => {
                    const pCode = parseDevicePartCode(vio.devicePart);
                    const vCode = parseViolatingConditionCode(vio.violatingCondition);
                    const rCode = parseSuggestedRemedyCode(vio.suggestedRemedy);

                    return (
                      <tr key={idx} className="border-t hover:bg-gray-50 transition">
                        <td className="px-4 py-2 whitespace-nowrap">{pCode}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{vCode}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{rCode}</td>
                        <td className="px-4 py-2">
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() => handleOpenModal(vio.violationId)}
                          >
                            View
                          </button>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{vio.violationId}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.inspectionNumber || 'N/A'}
                        </td>
                        {/* Building Site */}
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.buildingSite || 'N/A'}
                        </td>
                        {/* Agency */}
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.agency || 'N/A'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.violationDate || ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.violatingCondition || ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.itemStatus || ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.additionalComment || ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {vio.resolutionMethod || ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          ${Number(vio.estimatedAmount || 0).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* CARD VIEW */}
        {viewMode === 'card' && (
          <div className="m-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 overflow-auto">
            {filteredViolations.length === 0 ? (
              <p className="col-span-full text-center py-4">No violations yet</p>
            ) : (
              filteredViolations.map((vio, idx) => {
                const pCode = parseDevicePartCode(vio.devicePart);
                const vCode = parseViolatingConditionCode(vio.violatingCondition);
                const rCode = parseSuggestedRemedyCode(vio.suggestedRemedy);

                return (
                  <div
                    key={idx}
                    className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex flex-col"
                  >
                    <h3 className="text-md font-bold mb-2">
                      Violation #{vio.violationId}
                    </h3>
                    <p>Inspection #: {vio.inspectionNumber || 'N/A'}</p>
                    {/* Building & Agency in the card view */}
                    <p>Building: {vio.buildingSite || 'N/A'}</p>
                    <p>Agency: {vio.agency || 'N/A'}</p>
                    <p>Date: {vio.violationDate || 'N/A'}</p>
                    <p>
                      P: {pCode || 'N/A'}, V: {vCode || 'N/A'}, R: {rCode || 'N/A'}
                    </p>
                    <p>Status: {vio.itemStatus || 'N/A'}</p>
                    <p>Cost: ${Number(vio.estimatedAmount || 0).toFixed(2)}</p>

                    <button
                      className="mt-2 text-blue-600 hover:underline self-start"
                      onClick={() => handleOpenModal(vio.violationId)}
                    >
                      View
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* FOOTER => total cost */}
      {viewMode === 'table' && filteredViolations.length > 0 && (
        <div className="border-t border-gray-200 bg-white p-4 flex justify-end">
          <div className="font-bold text-lg">
            {deviceLabel
              ? `Total Cost for ${deviceLabel}: $${totalCost.toFixed(2)}`
              : `Total Cost: $${totalCost.toFixed(2)}`
            }
          </div>
        </div>
      )}

      {/* "View" Modal if you have it */}
      {isModalOpen && selectedViolationId && (
        <ViewViolationDeviceModal
          open={isModalOpen}
          onClose={handleCloseModal}
          violationId={selectedViolationId}
          // If the modal can also do editing => pass a refresh callback
          refreshList={() => {
            // Re-fetch from parent if needed:
            onViewViolations?.();
          }}
        />
      )}
    </div>
  );
}
